import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from '../../auth/staffAuthApi';
import { auth } from '../../auth/appAuth';
import { 
  Container, 
  Paper, 
  Typography, 
  Alert, 
  Box,
  alpha
} from '@mui/material';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GradientButton from '../../components/buttons/GradientButton';
import { useTheme } from '@mui/material/styles';

const SIGNUP_COMPLETE_KEY = 'signupComplete';

const PaymentPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    try {
      const data = localStorage.getItem('setupData');
      const signupComplete = sessionStorage.getItem(SIGNUP_COMPLETE_KEY);

      if (data) {
        setSetupData(JSON.parse(data));
        localStorage.removeItem('setupData');
      } else if (signupComplete) {
        setSetupData({ accountId: signupComplete });
      } else {
        throw new Error('No setup data found');
      }

      sessionStorage.removeItem('accountSetupInProgress');
      sessionStorage.removeItem(SIGNUP_COMPLETE_KEY);
      setLoading(false);
    } catch (err) {
      console.error('Setup processing failed:', err);
      setLoading(false);
      setError('Account setup failed. Please try again.');
    }
  }, []);

  const handleContinue = async () => {
    try {
      await signOut(auth);
    } catch (_) {
      // ignore — user may already be signed out after account creation
    }
    navigate('/app/login', {
      state: {
        verificationMessage:
          'Your account was created. Check your email for a verification link, then sign in to access the app.',
      },
    });
  };

  if (loading) {
    return <LoadingSpinner message="Setting up your account..." />;
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper 
          elevation={0}
          sx={{ 
            p: 4,
            textAlign: 'center',
            borderRadius: '16px',
            border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
            bgcolor: alpha(theme.palette.error.main, 0.02),
          }}
        >
          <ErrorOutlineIcon 
            sx={{ 
              fontSize: 64,
              color: 'error.main',
              mb: 3
            }}
          />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Payment Confirmation Expired
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
            Your payment confirmation session has expired. Please return to the setup page to complete your payment.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <GradientButton
              variant="error"
              size="large"
              onClick={() => navigate('/main/account-setup')}
            >
              Return to Setup
            </GradientButton>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 4,
          textAlign: 'center',
          borderRadius: '16px',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <CheckCircleIcon 
          sx={{ 
            fontSize: 64,
            color: 'primary.main',
            mb: 3
          }}
        />
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Account Setup Complete!
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Your account has been successfully created. Please check your email and verify your email address to access the app. Once verified, you can log in to start using our services.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <GradientButton
            variant="primary"
            size="large"
            onClick={handleContinue}
          >
            Go to Login
          </GradientButton>
        </Box>
      </Paper>
    </Container>
  );
};

export default PaymentPage; 